import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import type { CorrectionRequest } from "../lib/types";
import { Banner, CopyId } from "../components/ui";
import { statusColor } from "../lib/format";

const STATUSES = ["pending", "reviewed", "corrected", "rejected", "ignored"] as const;

export default function CorrectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [correction, setCorrection] = useState<CorrectionRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedStatus, setSavedStatus] = useState<string>("");
  const [adminNotes, setAdminNotes] = useState("");

  // Reusable loader: run on mount AND after a successful save (the update edge fn
  // returns {success:true}, not the record, so we must refetch to get fresh state).
  // savedStatus/adminNotes already hold the edits; we reset them from the refetched record.
  const fetchCorrection = useCallback(async () => {
    if (!id) return;
    const data = await invokeAdmin<CorrectionRequest>("admin_correction_get", { id });
    setCorrection(data);
    setSavedStatus(data.status);
    setAdminNotes(data.admin_notes ?? "");
  }, [id]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await fetchCorrection();
      } catch (e) {
        setError(errMsg(e));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [fetchCorrection]);

  // All hooks are declared above, so it's safe to bail on a missing route param
  // here (the /records/corrections/:id route always supplies one).
  if (!id) return null;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await invokeAdmin("admin_correction_update", { id, status: savedStatus, admin_notes: adminNotes });
      // update returns {success:true} — refetch to get fresh state.
      await fetchCorrection();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  const entityLink = correction
    ? correction.entity_type === "place"
      ? `/records/places/${correction.entity_id}`
      : `/records/events/${correction.entity_id}`
    : "#";

  if (loading) {
    return (
      <div>
        <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
          ← Records
        </Link>
        <p className="mt-4 text-sm text-zinc-500">Loading…</p>
      </div>
    );
  }

  if (error && !correction) {
    return (
      <div>
        <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
          ← Records
        </Link>
        <Banner className="mt-4">{error}</Banner>
      </div>
    );
  }

  if (!correction) return null;

  return (
    <div>
      <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
        ← Records
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Correction Request</h1>
          <div className="mt-0.5">
            <CopyId id={correction.id} chars={36} />
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {error && <Banner className="mt-3">{error}</Banner>}

      <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-zinc-500 uppercase">Entity type</span>
            <p className="mt-1 text-sm text-zinc-300">{correction.entity_type}</p>
          </div>
          <div>
            <span className="text-xs text-zinc-500 uppercase">Entity ID</span>
            <div className="mt-1 flex items-center gap-2">
              <CopyId id={correction.entity_id} chars={36} />
              <Link
                to={entityLink}
                className="text-xs text-zinc-400 hover:text-zinc-100 underline shrink-0"
              >
                View {correction.entity_type} →
              </Link>
            </div>
          </div>
          <div>
            <span className="text-xs text-zinc-500 uppercase">Created</span>
            <p className="mt-1 text-sm text-zinc-300">
              {new Date(correction.created_at).toLocaleString()}
            </p>
          </div>
          <div>
            <span className="text-xs text-zinc-500 uppercase">Current status</span>
            <div className="mt-1">
              <span className={`rounded px-1.5 py-0.5 text-xs ${statusColor(correction.status)}`}>
                {correction.status}
              </span>
            </div>
          </div>
        </div>

        <div>
          <span className="text-xs text-zinc-500 uppercase">Reason</span>
          <p className="mt-1 text-sm text-zinc-300">{correction.reason}</p>
        </div>

        {correction.details && (
          <div>
            <span className="text-xs text-zinc-500 uppercase">Details</span>
            <p className="mt-1 text-sm text-zinc-300 whitespace-pre-wrap">{correction.details}</p>
          </div>
        )}

        {correction.llm_reasoning && (
          <div>
            <span className="text-xs text-zinc-500 uppercase">LLM reasoning</span>
            <p className="mt-1 text-sm text-zinc-300 whitespace-pre-wrap">
              {correction.llm_reasoning}
            </p>
          </div>
        )}

        {correction.llm_confidence !== null && (
          <div>
            <span className="text-xs text-zinc-500 uppercase">LLM confidence</span>
            <p className="mt-1 text-sm text-zinc-300">{correction.llm_confidence}</p>
          </div>
        )}

        {correction.llm_correction_data && (
          <div>
            <span className="text-xs text-zinc-500 uppercase">LLM correction data</span>
            <pre className="mt-1 rounded bg-zinc-800 p-2 text-xs text-zinc-300 overflow-x-auto max-h-60 overflow-y-auto">
              {JSON.stringify(correction.llm_correction_data, null, 2)}
            </pre>
          </div>
        )}

        <hr className="border-zinc-800" />

        <div>
          <label className="text-xs text-zinc-500 uppercase">Status</label>
          <select
            value={savedStatus}
            onChange={(e) => setSavedStatus(e.target.value)}
            className="mt-1 block rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-zinc-500"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-zinc-500 uppercase">Admin notes</label>
          <textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            rows={4}
            placeholder="Internal notes…"
            className="mt-1 w-full rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
          />
        </div>
      </div>
    </div>
  );
}
